import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, BookMarked } from "lucide-react";
import { toast } from "sonner";

const CATS = ["incident", "sales", "onboarding", "security", "operations"];

export default function Playbooks() {
  const [items, setItems] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ category: "incident", steps: "" });

  const load = () => api.get("/playbooks").then((r) => setItems(r.data.playbooks));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title || !form.trigger || !form.steps) return toast.error("Título, disparador y pasos");
    await api.post("/playbooks", {
      title: form.title, trigger: form.trigger, category: form.category,
      steps: form.steps.split("\n").map((s) => s.trim()).filter(Boolean),
    });
    toast.success("Playbook creado");
    setForm({ category: "incident", steps: "" });
    setDlg(false);
    load();
  };

  return (
    <div className="p-8 space-y-6" data-testid="playbooks-page">
      <div className="border-b border-[var(--border)] pb-5 flex items-end justify-between">
        <div>
          <div className="label-mono mb-2">PROCEDIMIENTOS OPERATIVOS</div>
          <h1 className="font-display font-black text-4xl tracking-tight">Playbooks</h1>
          <p className="text-sm text-[var(--text-dim)] mt-1">
            Respuestas coreografiadas para incidentes, ventas y onboarding.
          </p>
        </div>
        <Dialog open={dlg} onOpenChange={setDlg}>
          <DialogTrigger asChild>
            <Button className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="pb-add">
              <Plus className="w-4 h-4 mr-2" /> Nuevo playbook
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none bg-[var(--surface)] border-[var(--border)] max-w-xl">
            <DialogHeader><DialogTitle className="font-display">Nuevo playbook</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="label-mono">Título</Label>
                <Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })}
                       className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="pb-title" />
              </div>
              <div>
                <Label className="label-mono">Disparador</Label>
                <Input value={form.trigger || ""} onChange={(e) => setForm({ ...form, trigger: e.target.value })}
                       placeholder="ej: security.incident con severity=critical"
                       className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="pb-trigger" />
              </div>
              <div>
                <Label className="label-mono">Pasos (uno por línea)</Label>
                <Textarea value={form.steps} onChange={(e) => setForm({ ...form, steps: e.target.value })}
                          placeholder="1. Aislar servicio&#10;2. Notificar CISO&#10;3. ..."
                          className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono min-h-[160px]" data-testid="pb-steps" />
              </div>
              <div>
                <Label className="label-mono">Categoría</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="pb-cat"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none bg-[var(--surface)] border-[var(--border)]">
                    {CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="pb-submit">Crear</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-[var(--border)] p-16 text-center" data-testid="pb-empty">
          <BookMarked className="w-8 h-8 mx-auto text-[var(--text-dim)] mb-3" />
          <p className="text-sm text-[var(--text-dim)]">
            Aún sin playbooks. Sugerencias: Respuesta a incidente crítico, Onboarding cliente enterprise, Fuga de credenciales.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((p) => (
            <div key={p.id} className="border border-[var(--border)] bg-[var(--surface)] p-5" data-testid={`pb-${p.id}`}>
              <div className="flex items-center justify-between mb-2">
                <Badge className="rounded-none bg-[var(--surface-2)] border border-[var(--border)] text-white hover:bg-[var(--surface-2)] font-mono text-[10px]">
                  {p.category}
                </Badge>
                <span className="font-mono text-[10px] text-[var(--text-dim)]">{p.steps.length} pasos</span>
              </div>
              <div className="font-display font-bold text-lg mb-1">{p.title}</div>
              <div className="text-xs text-[var(--text-dim)] mb-3">
                <span className="text-[var(--accent)] font-mono">TRIGGER:</span> {p.trigger}
              </div>
              <ol className="space-y-1">
                {p.steps.map((s, i) => (
                  <li key={i} className="text-xs text-white flex gap-2">
                    <span className="font-mono text-[var(--text-dim)] shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
